import React from 'react';
import { X, MapPin, Info, Camera, Hash } from 'lucide-react';
import { MediaUploader } from './MediaUploader';
import { createPost, getPosts } from '../../lib/media-service';
import { createMediaNotification } from '../../lib/notification-service';
import { budgetCountries, MAJOR_CITIES } from '../../data/budget-countries';
import { MEDIA_TAGS } from '../../data/media-tags';
import * as amplitude from '@amplitude/analytics-browser';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  username: string;
  userAvatar?: string;
}

const MAX_CAPTION_LENGTH = 2000;

export function CreatePostModal({ isOpen, onClose, userId, username, userAvatar }: CreatePostModalProps) {
  const [mediaUrl, setMediaUrl] = React.useState('');
  const [mediaType, setMediaType] = React.useState<'image' | 'video'>('image');
  const [caption, setCaption] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [city, setCity] = React.useState('');
  const [customCity, setCustomCity] = React.useState('');
  const [showCustomCity, setShowCustomCity] = React.useState(false);
  const [tags, setTags] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  const tagsByCategory = React.useMemo(() => {
    return Object.entries(MEDIA_TAGS).reduce((acc, [id, tag]) => {
      if (!acc[tag.category]) {
        acc[tag.category] = [];
      }
      acc[tag.category].push({ id, ...tag });
      return acc;
    }, {} as Record<string, Array<{ id: string; icon: string; label: string; category: string }>>);
  }, []);

  const categoryLabels: Record<string, string> = {
    status: 'Etapes PVT',
    logement: 'Logement',
    travail: 'Travail',
    lifestyle: 'Lifestyle',
    social: 'Social'
  };

  const handleUploadComplete = (url: string, type: 'image' | 'video') => {
    setMediaUrl(url);
    setMediaType(type);
    setError(null);
  };

  const handleUploadError = (errorMsg: string) => {
    setError(errorMsg);
  };

  const handleSelectTag = (tagId: string) => {
    if (!tags.includes(tagId) && tags.length < 5) {
      setTags([...tags, tagId]);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const validateForm = (): string | null => {
    if (!mediaUrl) return 'Veuillez selectionner une photo ou une video';
    if (!caption.trim()) return 'La description est obligatoire';
    if (!country) return 'Le pays est obligatoire';
    if (!city && !customCity) return 'La ville est obligatoire';
    if (tags.length === 0) return 'Veuillez selectionner au moins un tag';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const postId = await createPost({
        userId,
        username,
        avatar: userAvatar,
        mediaUrl,
        mediaType,
        caption,
        country,
        city: showCustomCity ? customCity : city,
        tags,
        likes: [],
        comments: []
      });

      amplitude.track("Photos Shared");

      (async () => {
        try {
          const existingPosts = await getPosts(userId);
          const followers = new Set<string>();
          existingPosts.forEach(p => {
            if (p.id === postId) return;
            p.likes?.forEach(likerUsername => {
              if (likerUsername !== username) followers.add(likerUsername);
            });
            p.comments?.forEach(c => {
              if (c.userId !== userId) followers.add(c.userId);
            });
          });
          followers.forEach(followerId => {
            createMediaNotification({
              recipientId: followerId,
              senderId: userId,
              senderUsername: username,
              type: 'media_new_post',
              postId,
              preview: caption,
            });
          });
        } catch (err) {
          console.error('Error sending new post notifications:', err);
        }
      })();

      onClose();
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Erreur lors de la creation du post');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]" style={{ position: 'fixed', overflow: 'hidden' }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-0 sm:p-4 animate-fade-in">
        <div className="relative w-full h-full sm:h-auto sm:max-h-[85vh] bg-white dark:bg-gray-800 sm:rounded-2xl shadow-xl sm:max-w-xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-900 dark:bg-white rounded-xl">
                  <Camera className="w-4 h-4 text-white dark:text-gray-900" />
                </div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  Creer un post
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-5 space-y-5">
              <form onSubmit={handleSubmit} className="space-y-5">
                {!mediaUrl ? (
                  <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-5 border border-gray-200/80 dark:border-gray-600/50">
                    <div className="flex items-center gap-2.5 mb-4">
                      <Camera className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Photo ou video
                      </span>
                    </div>
                    <MediaUploader
                      onUploadComplete={handleUploadComplete}
                      onUploadError={handleUploadError}
                      userId={userId}
                    />
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    {mediaType === 'image' ? (
                      <img src={mediaUrl} alt="Preview" className="w-full aspect-square object-cover" />
                    ) : (
                      <video src={mediaUrl} className="w-full aspect-square object-contain bg-black" controls />
                    )}
                    <button
                      type="button"
                      onClick={() => setMediaUrl('')}
                      className="absolute top-3 right-3 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-lg backdrop-blur-sm transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2.5 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-xl border border-red-200/50 dark:border-red-800/30">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-900 dark:text-white">
                      Description <span className="text-primary-500">*</span>
                    </label>
                    <span className={`text-xs tabular-nums ${
                      caption.length > MAX_CAPTION_LENGTH * 0.9
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`}>
                      {caption.length}/{MAX_CAPTION_LENGTH}
                    </span>
                  </div>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="w-full px-3.5 py-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm transition-all resize-none"
                    rows={3}
                    maxLength={MAX_CAPTION_LENGTH}
                    placeholder="Racontez votre histoire..."
                    required
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Localisation <span className="text-primary-500">*</span>
                  </label>
                  <select
                    value={country}
                    onChange={(e) => { setCountry(e.target.value); setCity(''); setCustomCity(''); setShowCustomCity(false); }}
                    className="w-full px-3.5 py-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm font-medium transition-all"
                    required
                  >
                    <option value="">Selectionnez un pays</option>
                    {budgetCountries.map((c) => (
                      <option key={c.code} value={c.code}>{c.name} {c.flag}</option>
                    ))}
                  </select>

                  {country && (
                    <div className="space-y-3 animate-fade-in">
                      <select
                        value={showCustomCity ? 'other' : city}
                        onChange={(e) => {
                          if (e.target.value === 'other') {
                            setShowCustomCity(true);
                            setCity('');
                          } else {
                            setShowCustomCity(false);
                            setCity(e.target.value);
                          }
                        }}
                        className="w-full px-3.5 py-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm font-medium transition-all"
                        required
                      >
                        <option value="">Selectionnez une ville</option>
                        {MAJOR_CITIES[country]?.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                        <option value="other">Autre ville...</option>
                      </select>

                      {showCustomCity && (
                        <input
                          type="text"
                          value={customCity}
                          onChange={(e) => setCustomCity(e.target.value)}
                          placeholder="Nom de la ville"
                          className="w-full px-3.5 py-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm font-medium transition-all animate-fade-in"
                          required
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <Hash className="w-4 h-4 text-gray-400" />
                    Tags <span className="text-primary-500">*</span>
                    <span className="text-xs font-normal text-gray-400 ml-1">(max. 5)</span>
                  </label>

                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tagId => {
                        const tag = MEDIA_TAGS[tagId];
                        return (
                          <span
                            key={tagId}
                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium"
                          >
                            <span>{tag.icon}</span>
                            <span>{tag.label}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tagId)}
                              className="p-0.5 hover:bg-white/20 dark:hover:bg-gray-900/20 rounded transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {tags.length < 5 && (
                    <div className="space-y-3">
                      {Object.entries(tagsByCategory).map(([category, categoryTags]) => (
                        <div key={category}>
                          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                            {categoryLabels[category] || category}
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {categoryTags.map(tag => (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => handleSelectTag(tag.id)}
                                disabled={tags.includes(tag.id)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  tags.includes(tag.id)
                                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 opacity-50'
                                    : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                              >
                                <span>{tag.icon}</span>
                                <span>{tag.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="sticky bottom-0 p-5 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
            <button
              type="submit"
              disabled={!mediaUrl || isSubmitting}
              className="w-full px-5 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-gray-200 disabled:to-gray-200 dark:disabled:from-gray-700 dark:disabled:to-gray-700 text-white disabled:text-gray-400 dark:disabled:text-gray-500 font-semibold rounded-xl transition-all shadow-lg shadow-primary-500/25 disabled:shadow-none flex items-center justify-center gap-2.5 disabled:cursor-not-allowed"
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Publication...</span>
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  <span>Publier</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
